/**
 * @class Bridge
 *
 * Базовый класс для обмена сообщениями между окнами или iframe с помощью `postMessage`.
 *
 * Реализует:
 * - Подписку на события `message` (через `init`).
 * - Распределение событий по обработчикам (`reducer`).
 * - Отправку сообщений родителю (`send`) и дочернему iframe (`sendToChild`).
 *
 * 📌 Слушатели сообщений задаются через объект `reducer` при вызове `init`.
 * Ключи в `reducer` соответствуют типам событий (строкам), которые возвращает `getEventType`.
 */
export default class Bridge {
  static _instance = null;

  /**
   * Получает единственный экземпляр Bridge (Singleton).
   *
   * @returns {Bridge} Экземпляр Bridge.
   */
  static get instance() {
    if (!this._instance) this._instance = new Bridge();
    return this._instance;
  }

  /**
   * Объект с обработчиками сообщений.
   * Ключи — строковые типы событий, значения — функции-обработчики.
   *
   * @type {Record<string, Function>}
   */
  reducer = {};

  /**
   * Флаг инициализации, чтобы избежать повторных вызовов init
   */
  _initialized = false;

  constructor() {
    this.onMessage = this.onMessage.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
  }

  /**
   * Инициализация слушателя событий.
   * Подписывается на события `message`, сохраняет переданный `reducer`
   * и отправляет событие готовности (`readyEvent`).
   *
   * @param {Record<string, Function>} reducer Набор обработчиков событий.
   * @returns {() => void} Функция для отписки от событий.
   */
  init(reducer = {}) {
    if (this._initialized) {
      // Обновляем reducer, но не отправляем повторно ready
      this.reducer = { ...this.reducer, ...reducer };
      return this.unsubscribe;
    }

    console.log('GA TMG [Bridge] 🔧 Инициализация Bridge');
    global.addEventListener('message', this.onMessage);

    this.reducer = reducer;
    this._initialized = true;

    this.send(this.readyEvent);
    this.send({ name: 'GET_APP_INFO' });

    return this.unsubscribe;
  }

  /**
   * Определяет тип события на основе объекта `event`.
   * Базовая реализация всегда возвращает `"unknownEvent"`,
   * переопределяется в наследниках (например, в CustomBridge).
   *
   * @param {MessageEvent} event Событие `message`.
   * @returns {"unknownEvent"} Тип события.
   */
  getEventType(event) {
    return 'unknownEvent';
  }

  /**
   * Отписка от событий `message`.
   */
  unsubscribe() {
    console.log('GA TMG [Bridge] 🔌 Отписка от событий');
    global.removeEventListener('message', this.onMessage);
    this._initialized = false;
  }

  /**
   * Обработчик событий `message`.
   * Определяет тип события через `getEventType`
   * и вызывает соответствующий обработчик из `reducer`, если он есть.
   *
   * @param {MessageEvent} event Событие `message`.
   */
  onMessage(event) {
    const type = this.getEventType(event);

    if (type !== 'unknownEvent') {
      console.log('GA TMG GET: ', {
        type,
        eventName: event?.data?.name,
        hasHandler: typeof this.reducer[type] === 'function',
      });

      if (typeof this.reducer[type] == 'function') {
        this.reducer[type](event);
      } else {
        console.log('GA TMG [Bridge] ⚠️ Нет обработчика для типа:', type);
      }
    }
  }

  /**
   * Значение события готовности, которое отправляется при инициализации.
   *
   * @returns {"ready"}
   */
  get readyEvent() {
    return 'ready';
  }

  /**
   * Отправляет сообщение родительскому окну или окну, которое открыло текущее.
   *
   * Логика:
   * - Если текущее окно открыто через `window.open`, сообщение уйдёт в `opener`.
   * - Если текущее окно встроено в `<iframe>`, сообщение уйдёт в `parent`.
   * - Если это обычная вкладка (нет opener, нет iframe), сообщение не отправляется.
   *
   * @param {any} message Сообщение для отправки (строка или объект).
   */
  send(message) {
    const parent = global.opener || global.parent;
    if (parent !== global.window) {
      console.log('GA TMG SEND: ', message);
      parent.postMessage(message, '*');
    }
  }

  /**
   * Отправляет сообщение во вложенный iframe.
   *
   * @param {HTMLIFrameElement} iframe Целевой iframe.
   * @param {any} message Сообщение для отправки (строка или объект).
   */
  sendToChild(iframe, message) {
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(message, '*');
    }
  }
}
